import { motion } from 'framer-motion';
import { Delete } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { springQuick, tapScale } from '../tokens';

type NumPadProps = {
  /** Initial value in dollars (or unit amounts). Defaults to 0. */
  initialValue?: number;
  /** Fires on every keystroke with the new numeric value. */
  onChange?: (value: number) => void;
  /** Fires when the user presses Done. */
  onDone?: (value: number) => void;
  /** Currency code for display formatting. Defaults to USD. */
  currency?: string;
  /** Locale for formatting. Defaults to 'en-US'. */
  locale?: string;
  /** Custom Done label (defaults to "Done"). */
  doneLabel?: string;
  /** Override the formatted display — useful for non-currency inputs (counts, percent). */
  formatValue?: (n: number) => string;
  /** Optional caption above the value (defaults to "Amount"). Pass null to hide. */
  caption?: string | null;
  /**
   * When true, value === 0 is treated as a valid choice — the Done key
   * stays active and triggers onDone(0). Used by the Spread NumPad,
   * where 0 means "Today, no spread". Defaults to false (the main
   * amount entry disables Done at zero).
   */
  allowZero?: boolean;
  className?: string;
};

const formatCurrency = (n: number, currency: string, locale: string) =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(n) ? n : 0);

const parseBuffer = (buf: string): number => {
  if (buf === '' || buf === '.') return 0;
  const n = Number(buf);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Inline financial input. Renders a formatted currency display above a
 * 4x4 keypad. Never opens the system keyboard — every digit must come
 * through these keys.
 *
 * Buffer rules:
 *   - Starts at "0".
 *   - Typing a digit while buffer is "0" (no decimal) replaces the 0.
 *   - "." is rejected if buffer already has a decimal.
 *   - Max 2 chars after the decimal point.
 *   - Backspace clamps to "0" when empty.
 */
export function NumPad({
  initialValue = 0,
  onChange,
  onDone,
  currency = 'USD',
  locale = 'en-US',
  doneLabel = 'Done',
  formatValue,
  caption = 'Amount',
  allowZero = false,
  className,
}: NumPadProps) {
  const [buffer, setBuffer] = useState<string>(() => {
    if (!initialValue) return '0';
    return initialValue.toFixed(2).replace(/\.?0+$/, '') || '0';
  });

  const value = parseBuffer(buffer);

  useEffect(() => {
    onChange?.(value);
    // Intentionally only fire when buffer changes; onChange identity is the caller's concern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buffer]);

  const press = (key: string) => {
    setBuffer((prev) => {
      switch (key) {
        case 'back': {
          if (prev.length <= 1) return '0';
          return prev.slice(0, -1);
        }
        case 'clear':
          return '0';
        case '.': {
          if (prev.includes('.')) return prev;
          return prev + '.';
        }
        case '00': {
          // Two-zero key. Reject if it would push past 2 decimals.
          if (prev.includes('.')) {
            const decimals = prev.split('.')[1] ?? '';
            if (decimals.length >= 1) return prev; // already 1-2 decimals
            return prev + '00';
          }
          if (prev === '0') return '0'; // don't grow leading zeros
          return prev + '00';
        }
        default: {
          // single digit 0-9
          if (prev.includes('.')) {
            const decimals = prev.split('.')[1] ?? '';
            if (decimals.length >= 2) return prev;
            return prev + key;
          }
          if (prev === '0') return key;
          return prev + key;
        }
      }
    });
  };

  const display = formatValue
    ? formatValue(value)
    : formatCurrency(value, currency, locale);
  // Done is disabled when the parsed value is non-positive AND the
  // caller hasn't opted into allowZero. parseFloat-style guard to
  // catch NaN / negative.
  const numericValue = Number.isFinite(value) ? value : 0;
  const doneDisabled = !allowZero && !(numericValue > 0);
  // Counter that bumps each time the user taps Done while disabled —
  // drives the disabled-state shake animation.
  const [shakeKey, setShakeKey] = useState(0);

  return (
    <div className={cn('flex flex-col', className)}>
      <div className="px-screen-x pt-2 pb-4 text-center">
        {caption ? (
          <div className="text-caption uppercase text-text-tertiary mb-label-gap">
            {caption}
          </div>
        ) : null}
        <div
          className={cn(
            'text-hero-sm font-light font-mono tabular-nums transition-colors duration-200',
            numericValue === 0
              ? 'text-text-tertiary/60'
              : 'text-text-primary',
          )}
        >
          {display}
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 px-screen-x pb-4">
        <Key onPress={() => press('1')}>1</Key>
        <Key onPress={() => press('2')}>2</Key>
        <Key onPress={() => press('3')}>3</Key>
        <Key onPress={() => press('back')} aria-label="Backspace">
          <Delete size={20} strokeWidth={1.5} />
        </Key>

        <Key onPress={() => press('4')}>4</Key>
        <Key onPress={() => press('5')}>5</Key>
        <Key onPress={() => press('6')}>6</Key>
        <Key onPress={() => press('.')}>.</Key>

        <Key onPress={() => press('7')}>7</Key>
        <Key onPress={() => press('8')}>8</Key>
        <Key onPress={() => press('9')}>9</Key>
        <Key onPress={() => press('clear')}>C</Key>

        <ZeroKey onPress={() => press('0')} onLongPress={() => press('00')} />
        <motion.button
          key={`done-${shakeKey}`}
          type="button"
          onClick={() => {
            if (doneDisabled) {
              setShakeKey((k) => k + 1);
              return;
            }
            onDone?.(numericValue);
          }}
          whileTap={doneDisabled ? undefined : tapScale}
          animate={
            doneDisabled && shakeKey > 0
              ? { x: [0, -6, 6, -6, 0] }
              : { x: 0 }
          }
          transition={
            doneDisabled && shakeKey > 0 ? { duration: 0.2 } : springQuick
          }
          aria-disabled={doneDisabled}
          className={cn(
            'h-14 rounded-button inline-flex items-center justify-center font-semibold',
            'text-[22px] tabular-nums col-span-2',
            doneDisabled
              ? 'bg-surface-secondary text-text-tertiary cursor-not-allowed'
              : 'bg-accent text-white',
          )}
        >
          {doneLabel}
        </motion.button>
      </div>
    </div>
  );
}

type KeyProps = {
  children: ReactNode;
  onPress: () => void;
  variant?: 'default' | 'primary';
  className?: string;
  'aria-label'?: string;
};

function Key({
  children,
  onPress,
  variant = 'default',
  className,
  'aria-label': ariaLabel,
}: KeyProps) {
  return (
    <motion.button
      type="button"
      aria-label={ariaLabel}
      onClick={onPress}
      whileTap={tapScale}
      transition={springQuick}
      className={cn(
        'h-14 rounded-button inline-flex items-center justify-center font-medium',
        'text-[22px] tabular-nums',
        variant === 'primary'
          ? 'bg-accent text-white'
          : 'bg-surface-secondary text-text-primary',
        className,
      )}
    >
      {children}
    </motion.button>
  );
}

/**
 * Zero key. Single tap inserts "0"; ~500ms hold inserts "00" — keeps
 * the calculator double-zero affordance without burning a separate cell
 * on the keypad. Spans two columns so the bottom row reads 0 | 0 | Save | Save.
 */
function ZeroKey({
  onPress,
  onLongPress,
}: {
  onPress: () => void;
  onLongPress: () => void;
}) {
  const timerRef = useRef<number | null>(null);
  const firedRef = useRef(false);

  const clear = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleDown = () => {
    firedRef.current = false;
    clear();
    timerRef.current = window.setTimeout(() => {
      firedRef.current = true;
      onLongPress();
    }, 500);
  };

  const handleUp = () => {
    clear();
    if (!firedRef.current) onPress();
  };

  const handleCancel = () => {
    clear();
    firedRef.current = true; // suppress the upcoming click() pseudo-event
  };

  return (
    <motion.button
      type="button"
      onPointerDown={handleDown}
      onPointerUp={handleUp}
      onPointerCancel={handleCancel}
      onPointerLeave={handleCancel}
      whileTap={tapScale}
      transition={springQuick}
      className={cn(
        'h-14 rounded-button inline-flex items-center justify-center font-medium',
        'text-[22px] tabular-nums col-span-2',
        'bg-surface-secondary text-text-primary',
      )}
    >
      0
    </motion.button>
  );
}
